'use client';

import { useEffect, useState } from 'react';
import type { Business } from '@/lib/types';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';

const isNonEmptyUrl = (url: string): boolean => url.trim().length > 0;

export interface UseLandingPageStepResult {
  selectedLandingPage: string;
  setSelectedLandingPage: (url: string) => void;
  isValid: boolean;
}

/**
 * State for the shared "pick a landing page" wizard step. Call it at the wizard's
 * top level, never inside the step's render branch, so the selection survives
 * navigating between steps and is available for the create payload. Defaults to the
 * business homepage, filling in once the business query resolves.
 */
export function useLandingPageStep(business?: Business | null): UseLandingPageStepResult {
  const [selectedLandingPage, setSelectedLandingPage] = useState<string>(business?.business_url ?? '');

  useEffect(() => {
    if (business?.business_url && !selectedLandingPage) {
      setSelectedLandingPage(business.business_url);
    }
  }, [business?.business_url]);

  return {
    selectedLandingPage,
    setSelectedLandingPage,
    isValid: isNonEmptyUrl(selectedLandingPage),
  };
}

export interface LandingPageStepProps {
  selectedUrl: string;
  onSelect: (url: string) => void;
  setStepValid: (valid: boolean) => void;
  instruction?: string;
}

/**
 * The shared landing-page wizard step: a short instruction plus the business
 * landing-page combobox. Pair with {@link useLandingPageStep}; while mounted the
 * step reports its own validity so the host wizard's Next button gates automatically.
 */
export function LandingPageStep({
  selectedUrl,
  onSelect,
  setStepValid,
  instruction = 'Choose the landing page for your campaign.',
}: LandingPageStepProps) {
  useEffect(() => {
    setStepValid(isNonEmptyUrl(selectedUrl));
  }, [selectedUrl, setStepValid]);

  return (
    <div className="space-y-4">
      <p className="text-gray-700">{instruction}</p>
      <BusinessLandingPageCombobox
        selectedUrl={selectedUrl || null}
        onSelect={(entry) => onSelect(entry?.url ?? '')}
        placeholder="Select a landing page..."
      />
    </div>
  );
}
